
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TourProvider } from './contexts/TourContext';
import { useAppContext } from './contexts/AppContext';
import { TourOverlay } from './components/TourOverlay';
import { MagnusAssistant } from './components/MagnusAssistant';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import PermissionGuard from './components/PermissionGuard';
import AutoGainSecurityPortal from './pages/Admin/AutoGainSecurityPortal';
import CustomerSecurityPortal from './pages/Admin/CustomerSecurityPortal';
import Dashboard from './pages/Dashboard';
import PDiagramPage from './pages/PDiagramPage';
import SystemDefinition from './pages/SystemDefinition';
import FMEAs from './pages/FMEAs';
import ProjectScopingPage from './pages/ProjectScoping/ProjectScopingPage';
import RiskAnalysis from './pages/RiskAnalysis';
import GraphViewPage from './pages/GraphViewPage';
import TraceabilityMatrix from './pages/TraceabilityMatrix';
import BomFunctions from './pages/BomFunctions';
import StructureTreePage from './pages/StructureTreePage';
import FunctionNetPage from './pages/FunctionNetPage';
import FailureNetPage from './pages/FailureNetPage';
import Dvpr from './pages/Dvpr';
import HelpModal from './components/HelpModal';
import FmeaEditorPage from './pages/FmeaEditor/FmeaEditorPage';
import ChatbotPage from './pages/ChatbotPage';
import FmeaGeneratorPage from './pages/FmeaGeneratorPage';
import MagnusGenesisPage from './pages/MagnusGenesisPage';
import ExportPage from './pages/ExportPage';
import ReliabilityTestPlan from './pages/ReliabilityTestPlan';
import ReliabilityLabPage from './pages/ReliabilityLabPage';
import PbsManager from './pages/PbsManager';
import TroubleshootingPage from './pages/Troubleshooting';
import AdminLoginPage from './pages/Admin/LoginPage';
import PermissionsDashboard from './pages/Admin/PermissionsDashboard';
import UserManagement from './pages/Admin/UserManagement';
import BotConfigPage from './pages/Admin/BotConfigPage';
import UserAnalytics from './pages/Admin/UserAnalytics';
import ComplianceChecker from './pages/Admin/ComplianceChecker';
import LinkExplorerPage from './pages/LinkExplorer';
import CustomerRequirementsPage from './pages/CustomerRequirements';
import ExternalAppPage from './pages/ExternalApp';
// import LandingPage from './pages/LandingPage';
import MaterialsDatabase from './pages/MaterialsDatabase';
import OctopartSearchPage from './pages/OctopartSearchPage';
import ECNManagement from './pages/ECNManagement';
import EpochManager from './pages/EpochManager';
import PhysicsLab from './pages/PhysicsLab';
import MethodologyComparison from './pages/MethodologyComparison';
import TreeVisualizer from './pages/Visualization/TreeVisualizer';
import FunctionalSafetyDashboard from './pages/FunctionalSafetyDashboard';
import ControlsManager from './pages/ControlsManager';
import ProjectManagement from './pages/ProjectManagement';
import ProjectCreator from './pages/ProjectCreator';
import PfmeaComingSoon from './pages/PfmeaComingSoon';
import ActionTrackingDashboard from './pages/ActionTrackingDashboard';
import WorkflowGatesPage from './pages/WorkflowGatesPage';
import ClientAdminDashboard from './pages/Admin/ClientAdminDashboard';
import Footer from './components/layout/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import SubscriptionManagementPage from './pages/Admin/SubscriptionManagementPage';
import PlatformAnalyticsPage from './pages/Admin/PlatformAnalyticsPage';
import LimsPage from './pages/LimsPage';
import ProductManagement from './pages/ProductManagement';
import CustomerManagement from './pages/CustomerManagement';
import UserGuidePage from './pages/UserGuidePage';
import PendingAccessPage from './pages/PendingAccessPage';
import MagnusLearningCenter from './pages/MagnusLearningCenter';
import AdvancedConvergencePage from './pages/AdvancedConvergencePage';

// Controller to handle auto-starting the tour for non-admin users
// DISABLED: Tour is now triggered via Dashboard welcome modal for better UX
// The modal gives users the choice to start tour or dismiss
const TourController: React.FC = () => {
    // Keeping this component for future use if needed
    // Tour is now controlled by Dashboard modal for non-admin users
    return null;
};

// A wrapper for protected routes that checks for specific roles
const ProtectedRoute: React.FC<{
    children: React.ReactNode;
    requiredRole?: 'autoGainAdmin' | 'clientAdmin' | 'projectManager' | 'engineer' | 'viewer';
}> = ({ children, requiredRole }) => {
    const { currentUser } = useAppContext();

    if (!currentUser) {
        return <Navigate to="/admin/login" replace />;
    }

    // ISO 27001 Gatekeeper: Block Pending Users
    // Only AutoGain Admin bypasses approval check (fail-safe)
    if ((currentUser.role === 'pending' || currentUser.isApproved === false) && currentUser.role !== 'autoGainAdmin') {
        return <Navigate to="/access-pending" replace />;
    }

    if (requiredRole) {
        return (
            <PermissionGuard requiredRole={requiredRole} showMessage={true}>
                {children}
            </PermissionGuard>
        );
    }

    return <>{children}</>;
};

import { LearnProvider } from './context/LearnContext';
import { LearnSidebar } from './components/learn/LearnSidebar';

const App: React.FC = () => {
    const { theme, currentUser, isFullScreen } = useAppContext();

    React.useEffect(() => {
        const root = document.documentElement;
        root.classList.remove('light', 'dark', 'high-contrast');
        root.classList.add(theme);
        const themeColors = {
            light: {
                '--color-primary': '#3f51b5',
                '--color-secondary': '#303f9f',
                '--color-primary-highlight': '#e8eaf6',
                '--color-background': '#f5f5f5',
                '--color-card': '#ffffff',
                '--color-text-primary': '#263238',
                '--color-text-secondary': '#546e7a',
                '--color-border': '#cfd8dc',
                '--color-sidebar': '#ffffff',
                '--color-sidebar-text': '#37474f',
                '--color-chip-prev': '#fb8c00',
                '--color-chip-prev-bg': '#fff7e6',
                '--color-chip-det': '#1e88e5',
                '--color-chip-det-bg': '#e7f0ff',
            },
            dark: {
                '--color-primary': '#6366f1',        // Indigo-500 for better dark mode contrast
                '--color-secondary': '#4f46e5',      // Indigo-600
                '--color-primary-highlight': '#312e81', // Indigo-900
                '--color-background': '#0f172a',     // Slate-900 - Deep dark background
                '--color-card': '#1e293b',           // Slate-800 - Card background with transparency
                '--color-text-primary': '#f1f5f9',   // Slate-100 - Primary text
                '--color-text-secondary': '#94a3b8', // Slate-400 - Secondary text
                '--color-border': '#334155',         // Slate-700 - Borders
                '--color-sidebar': '#1e293b',        // Slate-800 - Sidebar background
                '--color-sidebar-text': '#e2e8f0',   // Slate-200 - Sidebar text
                '--color-chip-prev': '#fb8c00',      // Keep orange for prevention
                '--color-chip-prev-bg': '#451a03',   // Dark orange bg
                '--color-chip-det': '#60a5fa',       // Blue-400 for detection
                '--color-chip-det-bg': '#1e3a8a',    // Dark blue bg
            },
            'high-contrast': {
                '--color-primary': '#0000ff',
                '--color-secondary': '#ff00ff',
                '--color-primary-highlight': '#ffff00',
                '--color-background': '#000000',
                '--color-card': '#000000',
                '--color-text-primary': '#ffffff',
                '--color-text-secondary': '#eeeeee',
                '--color-border': '#ffffff',
                '--color-sidebar': '#000000',
                '--color-sidebar-text': '#ffffff',
                '--color-chip-prev': '#ffff00',
                '--color-chip-prev-bg': '#333300',
                '--color-chip-det': '#00ffff',
                '--color-chip-det-bg': '#003333',
            },
        };
        const colors = themeColors[theme as keyof typeof themeColors];
        for (const [key, value] of Object.entries(colors)) {
            root.style.setProperty(key, value);
        }
    }, [theme]);

    // =========================================================================
    // ISO 27001 CRITICAL SECURITY CHECK
    // This MUST happen BEFORE rendering ANY UI (Sidebar, Header, etc.)
    // =========================================================================

    // Check 1: No user at all - redirect to login
    if (!currentUser) {
        return (
            <Routes>
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="*" element={<Navigate to="/admin/login" replace />} />
            </Routes>
        )
    }

    // Check 2: User exists but NOT APPROVED - show ONLY the pending page
    // This is the critical gatekeeper - NO sidebar, NO header, NO navigation
    const isApprovedUser = currentUser.isApproved === true && currentUser.role !== 'pending';
    const isAutoGainAdmin = currentUser.role === 'autoGainAdmin';

    if (!isApprovedUser && !isAutoGainAdmin) {
        return (
            <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-100'}`}>
                <Routes>
                    <Route path="/admin/login" element={<AdminLoginPage />} />
                    <Route path="*" element={<PendingAccessPage />} />
                </Routes>
            </div>
        )
    }

    return (
        <LearnProvider>
            <TourProvider>
                <TourController />
                <div className={`flex h-screen text-text-primary relative ${theme === 'dark' ? 'bg-slate-900' : 'bg-background'}`}>
                    {/* Background gradients for dark mode glassmorphism */}
                    {theme === 'dark' && (
                        <div className="fixed inset-0 overflow-hidden pointer-events-none">
                            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
                            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-3xl" />
                        </div>
                    )}

                    {!isFullScreen && <Sidebar />}
                    <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                        <Header />
                        <main className={`flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 lg:p-8 ${theme === 'dark'
                            ? 'bg-slate-900/50 backdrop-blur-sm'
                            : 'bg-background'
                            }`}>
                            <Routes>
                                {/* ... routes unchanged ... */}
                                <Route path="/" element={<Navigate to="/dashboard" />} />
                                <Route path="/access-pending" element={<PendingAccessPage />} />
                                <Route path="/admin/login" element={<Navigate to="/dashboard" />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/learning-center" element={<MagnusLearningCenter />} />
                                <Route path="/user-guide" element={<UserGuidePage />} />
                                <Route path="/ai-assistant" element={<ChatbotPage />} />
                                <Route path="/auto-fmea" element={<FmeaGeneratorPage />} />
                                <Route path="/magnus-prime" element={<MagnusGenesisPage />} />
                                <Route path="/advanced-convergence" element={<AdvancedConvergencePage />} />
                                <Route path="/p-diagram" element={<PDiagramPage />} />
                                <Route path="/system-definition" element={<SystemDefinition />} />
                                <Route path="/project-scoping" element={<ProjectScopingPage />} />
                                <Route path="/fmeas" element={<ErrorBoundary><FMEAs /></ErrorBoundary>} />
                                <Route path="/fmea-editor" element={<ErrorBoundary><FmeaEditorPage /></ErrorBoundary>} />
                                <Route path="/risk-analysis" element={<RiskAnalysis />} />
                                <Route path="/graph-view" element={<GraphViewPage />} />
                                <Route path="/link-explorer" element={<LinkExplorerPage />} />
                                <Route path="/traceability-matrix" element={<ErrorBoundary><TraceabilityMatrix /></ErrorBoundary>} />
                                <Route path="/bom-functions" element={<BomFunctions />} />
                                <Route path="/structure-tree" element={<StructureTreePage />} />
                                <Route path="/function-net" element={<FunctionNetPage />} />
                                <Route path="/failure-net" element={<FailureNetPage />} />
                                <Route path="/materials-database" element={<MaterialsDatabase />} />
                                <Route path="/component-search" element={<OctopartSearchPage />} />
                                <Route path="/ecn-management" element={<ECNManagement />} />
                                <Route path="/dvpr" element={<Dvpr />} />
                                <Route path="/reliability-plan" element={<ReliabilityTestPlan />} />
                                <Route path="/reliability-lab" element={<ReliabilityLabPage />} />
                                <Route path="/lims" element={<LimsPage />} />
                                <Route path="/export" element={<ExportPage />} />
                                <Route path="/pbs-manager" element={<PbsManager />} />
                                <Route path="/troubleshooting" element={<TroubleshootingPage />} />
                                <Route path="/customer-requirements" element={<CustomerRequirementsPage />} />
                                <Route path="/external-app" element={<ExternalAppPage />} />
                                <Route path="/methodology-comparison" element={<MethodologyComparison />} />
                                <Route path="/epoch-manager" element={<EpochManager />} />
                                <Route path="/projects" element={<ProjectManagement />} />
                                <Route path="/project-creator" element={<ProjectCreator />} />
                                <Route path="/products" element={<ProductManagement />} />
                                <Route path="/customers" element={<CustomerManagement />} />
                                <Route path="/physics-verification" element={<PhysicsLab />} />
                                <Route path="/action-tracking" element={<ActionTrackingDashboard />} />
                                <Route path="/workflow-gates" element={<WorkflowGatesPage />} />
                                <Route path="/visualization/tree" element={<TreeVisualizer />} />

                                <Route path="/functional-safety" element={<FunctionalSafetyDashboard />} />
                                <Route path="/controls-library" element={<ControlsManager />} />
                                <Route path="/pfmea/coming-soon" element={<PfmeaComingSoon />} />


                                {/* Platform Admin Routes (AutoGain Staff) */}
                                <Route
                                    path="/platform"
                                    element={
                                        <ProtectedRoute requiredRole="autoGainAdmin">
                                            <AutoGainSecurityPortal />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/platform/clients"
                                    element={
                                        <ProtectedRoute requiredRole="autoGainAdmin">
                                            <ClientAdminDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                {/* We can add more platform routes here as they are created */}
                                {/* We can add more platform routes here as they are created */}
                                <Route
                                    path="/platform/subscriptions"
                                    element={
                                        <ProtectedRoute requiredRole="autoGainAdmin">
                                            <SubscriptionManagementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/platform/analytics" element={<ProtectedRoute requiredRole="autoGainAdmin"><PlatformAnalyticsPage /></ProtectedRoute>} />
                                <Route path="/platform/debug" element={<ProtectedRoute requiredRole="autoGainAdmin"><div className="p-8">System Debug Mode</div></ProtectedRoute>} />

                                {/* Customer Admin Routes (Client Admins) */}
                                <Route
                                    path="/admin/security"
                                    element={
                                        <ProtectedRoute requiredRole="clientAdmin">
                                            <CustomerSecurityPortal />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/users"
                                    element={
                                        <ProtectedRoute requiredRole="clientAdmin">
                                            <UserManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/permissions"
                                    element={
                                        <ProtectedRoute requiredRole="clientAdmin">
                                            <PermissionsDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/bots"
                                    element={
                                        <ProtectedRoute requiredRole="clientAdmin">
                                            <BotConfigPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/analytics"
                                    element={
                                        <ProtectedRoute requiredRole="clientAdmin">
                                            <UserAnalytics />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/compliance"
                                    element={
                                        <ProtectedRoute requiredRole="clientAdmin">
                                            <ComplianceChecker />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="*" element={<Navigate to="/dashboard" />} />
                            </Routes>
                        </main>
                        <Footer />
                    </div>
                </div>
                <HelpModal />
                <TourOverlay />
                <LearnSidebar />
                {/* Magnus Assistant - Only show if user has permission */}
                {(currentUser?.role === 'admin' || currentUser?.role === 'autoGainAdmin' || currentUser?.role === 'magnus' || currentUser?.permissions.includes('magnus-assistant') || currentUser?.permissions.includes('*')) && (
                    <MagnusAssistant />
                )}
            </TourProvider>
        </LearnProvider>
    );
};

export default App;
